if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

function inViewport(rect) {
  return rect.bottom > 0 && rect.top < window.innerHeight;
}

if (!customElements.get('theme-header')) {
  const SLIDESHOW_TAG = 'vertical-slideshow';
  const SLIDE_CLASS = '.vertical-slideshow--slide';
  const RETRY_DELAY_MS = 500;
  const LATE_UPDATE_MS = 800;
  const LATE_UPDATE_MS_ANIM = 1500;

  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.header_el = null;

      document.addEventListener('keyup', this.onEscapeKey.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this));
      this.setupSlideshowVisibility();

      if (this.menu) {
        this.menu.querySelectorAll('summary').forEach((s) => s.addEventListener('click', this.onSummaryClick.bind(this)));
        this.menu.querySelectorAll('.parent-link-back--button').forEach((b) => b.addEventListener('click', this.onCloseButtonClick.bind(this)));
      }
    }

    getHeaderEl() {
      if (!this.header_el && this.header_section) {
        this.header_el = this.header_section.querySelector('.header');
      }
      return this.header_el;
    }

    getHeaderForTransparency() {
      return this.classList.contains('header') ? this : this.querySelector('.header');
    }

    setupSlideshowVisibility() {
      const header = this.getHeaderForTransparency();
      const isHome = document.body.classList.contains('template-index') ||
        (window.location.pathname === '/' || window.location.pathname === '/index');
      if (!isHome || !header || !header.classList.contains('transparent--true')) return;

      const tryRun = () => {
        const slideshow = document.querySelector(SLIDESHOW_TAG);
        const slides = slideshow && slideshow.querySelectorAll(SLIDE_CLASS);
        if (!slides || slides.length === 0) return false;

        const setVisible = (visible) => {
          if (document.documentElement.classList.contains('menu-open')) return;
          if (visible) {
            header.classList.add('is-sticky', 'is-sticky-active');
          } else {
            header.classList.remove('is-sticky', 'is-sticky-active');
          }
        };

        const update = () => {
          const slideshowRect = slideshow.getBoundingClientRect();
          const inSlideshowView = inViewport(slideshowRect);
          let onSlide = false;
          for (let i = 0; i < slides.length; i++) {
            if (inViewport(slides[i].getBoundingClientRect())) {
              onSlide = true;
              break;
            }
          }
          setVisible(!inSlideshowView || !onSlide);
        };

        header.classList.add('is-sticky', 'is-sticky-active');
        const onScrollOrResize = debounce(update, 16);
        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });
        update();
        const lateMs = document.body.classList.contains('animations-true') ? LATE_UPDATE_MS_ANIM : LATE_UPDATE_MS;
        setTimeout(update, lateMs);
        return true;
      };

      const runWhenReady = (fn) => {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', fn);
        } else {
          fn();
        }
      };

      runWhenReady(() => {
        if (!tryRun()) setTimeout(tryRun, RETRY_DELAY_MS);
      });
    }

    onEscapeKey(e) {
      if (e.code && e.code.toUpperCase() !== 'ESCAPE') return;
      if (!document.documentElement.classList.contains('menu-open')) return;
      document.documentElement.classList.remove('menu-open');
      document.body.classList.remove('overflow-hidden');
    }

    onDocumentClick(e) {
      const btn = e.target.closest && e.target.closest('.mobile-toggle');
      if (!btn) return;

      e.preventDefault();
      const wrapper = document.querySelector('.mobile-toggle-wrapper');
      const opening = !document.documentElement.classList.contains('menu-open');

      if (opening) {
        this.openMobileMenu(wrapper);
      } else {
        this.closeMobileMenu(wrapper);
      }
      window.dispatchEvent(new Event('resize.resize-select'));
    }

    openMobileMenu(wrapper) {
      document.documentElement.classList.add('menu-open');
      document.body.classList.add('overflow-hidden');

      if (wrapper) {
        wrapper.classList.add('active');
        try { wrapper.setAttribute('open', ''); } catch (_) {}
      }

      const header = this.getHeaderEl();
      if (header) header.classList.add('is-sticky', 'is-sticky-active');

      if (this.header_section && typeof this.header_section.scrollIntoView === 'function') {
        this.header_section.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      const mainContent = document.getElementById('main-content');
      if (mainContent && this.header_section) {
        this._mainContentPaddingRestore = mainContent.style.paddingTop;
        mainContent.style.paddingTop = this.header_section.offsetHeight + 'px';
      }
    }

    closeMobileMenu(wrapper) {
      const mainContent = document.getElementById('main-content');
      if (mainContent && this._mainContentPaddingRestore !== undefined) {
        mainContent.style.paddingTop = this._mainContentPaddingRestore;
        this._mainContentPaddingRestore = undefined;
      }

      document.documentElement.classList.remove('menu-open');
      document.body.classList.remove('overflow-hidden');

      if (wrapper) {
        wrapper.classList.remove('active');
        try { wrapper.removeAttribute('open'); } catch (_) {}
      }

      const header = this.getHeaderEl();
      if (header) header.classList.remove('is-sticky', 'is-sticky-active');
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        if (parentMenuElement) parentMenuElement.classList.add('submenu-open');
      }, 100);
    }

    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;
      const handleAnimation = (time) => {
        if (animationStart === undefined) animationStart = time;
        if (time - animationStart < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };
      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu > .menu-item-has-children:not(.menu-item-has-megamenu) > .sub-menu');
      if (!this.submenus.length) return;

      const boundResize = () => this.resizeSubMenus();
      window.addEventListener('resize', debounce(boundResize, 100));
      window.dispatchEvent(new Event('resize'));
      document.fonts.ready.then(boundResize);
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        submenu.querySelectorAll(':scope > .menu-item-has-children > .sub-menu').forEach((sub) => {
          const w = sub.offsetWidth;
          const parent = sub.parentElement.parentElement;
          const l = parent.getBoundingClientRect().left + parent.clientWidth + 10;
          const total = w + l;
          sub.parentElement.classList.toggle('left-submenu', total > document.body.clientWidth);
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
